import { Component, OnInit, inject } from '@angular/core';

import { NgxSpinnerService } from 'ngx-spinner';
import { finalize } from 'rxjs/operators';

import { saveAs } from 'file-saver';
import { NZ_MODAL_DATA, NzModalRef } from 'ng-zorro-antd/modal';

import { ErrorsHandlingService } from '../../../../core/services';

import { MainService } from '../../services';

@Component({
  selector: 'app-block-allow-endpoints-dialog',
  templateUrl: './block-allow-endpoints-dialog.component.html',
  styleUrls: ['./block-allow-endpoints-dialog.component.scss'],
  standalone: false
})
export class BlockAllowEndpointsDialogComponent implements OnInit {
  protected isShowMessage = false;
  protected message = '';
  protected isFileUploading = false;
  protected clickSaveButton = false;
  protected isFileDownloading = false;

  public data = inject(NZ_MODAL_DATA);

  private list: string[] = [];
  private countItems = 0;
  private countItemsBeforeUpload = 0;
  private countUploadFile: number;

  constructor(
    private dialogRef: NzModalRef<BlockAllowEndpointsDialogComponent>,
    private loadingService: NgxSpinnerService,
    private errorsHandlingService: ErrorsHandlingService,
    private mainService: MainService,
  ) { }

  public ngOnInit() {
    this.initializeCountsAndTypes();
  }

  protected shouldClose(): boolean | void {
    if (this.isFileUploading && !confirm('File upload is still in progress, are you sure you want to stop it?')) {
      return false;
    }
    this.dialogRef.close({
      itemsCount: this.countItemsBeforeUpload,
      type: this.data.type,
      list: [],
      allowBlockType: this.data.allowBlockType
    });
  }

  protected fileUpload($event: Event) {
    this.loadingService.show();
    this.isFileUploading = true;

    const input = $event.target as HTMLInputElement;
    this.readThis(input);

    const files = input.files;
    if (!files || files.length === 0) {
      this.loadingService.hide();
      this.isFileUploading = false;
      return;
    }

    this.mainService.parseFile(files[0], this.data.listType)
      .pipe(finalize(() => this.loadingService.hide()))
      .subscribe(
        (resp) => this.handleFileUploadSuccess(resp, files[0].name),
        (error) => this.handleFileUploadError(error)
      );
  }

  protected closeDialog() {
    this.isShowMessage = false;
  }

  protected saveData() {
    if (this.list.length === 0) {
      this.errorsHandlingService.showNotification('error', 'error', 'error', 'Please upload file or use Cancel button', null);
      return;
    }

    this.clickSaveButton = true;
    const listType = this.getListType();

    this.mainService.setList(this.data.endpointType, this.data.endpoint.id, listType, this.list)
      .subscribe(
        () => this.handleSaveSuccess(),
        (error) => this.handleSaveError(error)
      );
  }

  protected listDownload() {
    this.isFileDownloading = true;
    const listType = this.getListType();

    this.loadingService.show();
    this.mainService.downLoadList(this.data.endpointType, this.data.endpoint.id, listType)
      .pipe(finalize(() => {
        this.loadingService.hide();
        this.isFileDownloading = false;
      }))
      .subscribe(
        response => this.handleDownloadSuccess(response),
        error => this.handleDownloadError(error)
      );
  }

  protected deleteList() {
    if (this.countItemsBeforeUpload > 0) {
      this.performDelete();
    } else {
      this.closeDialogWithEmptyList();
    }
  }

  private performDelete(): void {
    this.loadingService.show();
    this.mainService.deleteFilterList(this.data.endpointType, this.data.endpoint.id, this.data.type)
      .pipe(finalize(() => this.loadingService.hide()))
      .subscribe(
        (resp) => this.handleDeleteSuccess(resp),
        (error) => this.errorsHandlingService.handleError(error)
      );
  }

  private handleDeleteSuccess(resp: any): void {
    if (resp.status === 'success') {
      this.countItems = 0;
      this.closeDialogWithEmptyList();
    }
  }

  private closeDialogWithEmptyList(): void {
    this.dialogRef.close({
      itemsCount: this.countItems,
      type: this.data.type,
      list: [],
      allowBlockType: this.data.allowBlockType
    });
  }

  private readThis(inputValue: HTMLInputElement): void {
    const file: File = inputValue.files[0];
    const reader: FileReader = new FileReader();
    reader.onloadend = () => {
      const lines = (<string>reader.result).split('\n').map(line => {
        return line;
      });
      this.countUploadFile = lines.length;
    };
    reader.readAsText(file);
  }

  private handleFileUploadSuccess(resp: any, fileName: string): void {
    const fileExtension = fileName.split('.').pop();

    resp.items.forEach(item => {
      if (item !== '') {
        this.list.push(item);
      }
    });

    if (this.list.length > 0) {
      this.updateUploadMessage(fileExtension, resp.items);
    }

    this.isFileUploading = false;
  }

  private handleFileUploadError(error: any): void {
    this.isFileUploading = false;
    this.errorsHandlingService.handleError(error);
  }

  private updateUploadMessage(fileExtension: string, items: any[]): void {
    this.isShowMessage = true;
    this.countItems = this.data.countListItems = this.list.length;
    this.message = `Was added ${this.countItems} unique items from ${this.countUploadFile}`;

    if (fileExtension === 'xlsx') {
      this.list = items.flat();
    }
  }

  private getListType(): string {
    if (this.data.allowBlockType === 'Blocked' && this.data.endpointType === 'dsp') {
      return `blocked-${this.data.type}`;
    }
    return this.data.type;
  }

  private handleSaveSuccess(): void {
    this.clickSaveButton = false;
    this.dialogRef.close({
      itemsCount: this.countItems,
      type: this.data.type,
      list: this.list,
      allowBlockType: this.data.allowBlockType,
    });
  }

  private handleSaveError(error: any): void {
    this.errorsHandlingService.handleError(error);
    this.clickSaveButton = false;
  }

  private handleDownloadSuccess(response: any): void {
    saveAs(response, `${this.data.type}.txt`);
  }

  private handleDownloadError(error: any): void {
    this.errorsHandlingService.handleError(error);
  }

  private initializeCountsAndTypes(): void {
    this.countItems = this.data.countListItems;
    this.countItemsBeforeUpload = this.data.countListItems;
  }
}
